import { useEffect, useRef, useState } from "react";
import { Share2, Download, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtDur = (ms) => {
  const m = Math.max(1, Math.round(ms / 60000));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};

function drawCard(canvas, { title, distanceKm, stops, totalStops, durationMs, date }) {
  const W = 1080, H = 1350;
  canvas.width = W; canvas.height = H;
  const c = canvas.getContext("2d");
  c.fillStyle = "#001A4D"; c.fillRect(0, 0, W, H);
  c.fillStyle = "#2563EB"; c.fillRect(0, 0, W, 18);
  // grain-ish dots
  c.fillStyle = "rgba(255,255,255,0.04)";
  for (let i = 0; i < 1400; i++) c.fillRect(Math.random() * W, Math.random() * H, 3, 3);

  c.fillStyle = "#F5F5F0";
  c.font = "600 30px 'JetBrains Mono', monospace";
  c.fillText("WHANGAMATĀ CYCLES · SELF-GUIDED", 80, 130);
  c.font = "300 96px Georgia, 'Times New Roman', serif";
  const words = title.split(" "); let line = "", y = 260;
  words.forEach((w) => {
    const t = line ? `${line} ${w}` : w;
    if (c.measureText(t).width > W - 160) { c.fillText(line, 80, y); y += 104; line = w; } else line = t;
  });
  c.fillText(line, 80, y); y += 60;
  c.fillStyle = "rgba(245,245,240,0.6)";
  c.font = "34px Georgia, serif";
  c.fillText(`Ride complete · ${date}`, 80, y + 30);

  const stats = [
    [`${distanceKm.toFixed(1)} km`, "distance ridden"],
    [`${stops}/${totalStops}`, "stops visited"],
    [fmtDur(durationMs), "time on the ride"],
  ];
  let sy = y + 160;
  stats.forEach(([v, l]) => {
    c.fillStyle = "rgba(255,255,255,0.06)";
    c.beginPath(); c.roundRect(80, sy, W - 160, 190, 28); c.fill();
    c.fillStyle = "#F5F5F0";
    c.font = "700 92px 'JetBrains Mono', monospace";
    c.fillText(v, 120, sy + 118);
    c.fillStyle = "#93C5FD";
    c.font = "600 28px 'JetBrains Mono', monospace";
    c.fillText(l.toUpperCase(), 120, sy + 162);
    sy += 220;
  });

  c.fillStyle = "#F5F5F0";
  c.font = "700 36px Georgia, serif";
  c.fillText("Ride your own way. We'll tell the stories.", 80, H - 120);
  c.fillStyle = "rgba(245,245,240,0.5)";
  c.font = "28px 'JetBrains Mono', monospace";
  c.fillText("#whangamatacycles", 80, H - 70);
}

export const RideSummary = ({ route, stats, onClose }) => {
  const canvasRef = useRef(null);
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);
  const data = {
    title: route.title,
    distanceKm: (stats.distanceM > 200 ? stats.distanceM / 1000 : route.distance_km) || 0,
    stops: stats.visited,
    totalStops: route.stops.length,
    durationMs: Date.now() - stats.startedAt,
    date: new Date().toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" }),
  };

  useEffect(() => {
    drawCard(canvasRef.current, data);
    setImg(canvasRef.current.toDataURL("image/png"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toFile = () => new Promise((res) => canvasRef.current.toBlob((b) => res(new File([b], "whangamata-ride.png", { type: "image/png" })), "image/png"));
  const share = async () => {
    setBusy(true);
    try {
      const file = await toFile();
      const text = `I just rode the ${route.title} with Whangamatā Cycles — ${data.distanceKm.toFixed(1)}km, ${data.stops} stops, ${fmtDur(data.durationMs)}. #whangamatacycles`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: "My ride" });
      } else if (navigator.share) {
        await navigator.share({ text, url: window.location.origin });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Caption copied — download the card to post it");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Sharing isn't available here — download the card instead");
    } finally { setBusy(false); }
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = img; a.download = "whangamata-ride.png"; a.click();
  };

  return (
    <div data-testid="ride-summary" className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <canvas ref={canvasRef} className="hidden" />
      <div className="bg-[#F5F5F0] rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 pt-4">
          <p className="overline text-[#001A4D]">Ride complete</p>
          <button data-testid="ride-summary-close" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-4 pt-3">
          {img && <img data-testid="ride-summary-card" src={img} alt="Ride summary card" className="w-full rounded-xl shadow-lg" />}
        </div>
        <p data-testid="ride-summary-stats" className="px-4 pt-3 text-sm text-black/60 text-center font-mono">
          {data.distanceKm.toFixed(1)} km · {data.stops}/{data.totalStops} stops · {fmtDur(data.durationMs)}
        </p>
        <div className="flex gap-2 p-4">
          <button data-testid="ride-share-btn" onClick={share} disabled={busy} className="flex-1 h-12 rounded-full bg-[#001A4D] hover:bg-[#00287A] text-white font-medium flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share
          </button>
          <button data-testid="ride-download-btn" onClick={download} className="h-12 px-5 rounded-full border border-black/15 font-medium flex items-center gap-2">
            <Download className="w-4 h-4" /> Save
          </button>
        </div>
      </div>
    </div>
  );
};
